/* eslint complexity: ["error", {max: 9}] -- formatFieldValue covers all FormFieldType values */
import { isNil } from "lodash";

import { DateFormat } from "@/constants/DateFormat";
import type { FormFieldTypeValue } from "@/ui/composed/form/types";
import { FormFieldType } from "@/ui/composed/form/types";
import Text from "@/ui/core/Text";
import { ColorToken } from "@/ui/theme/tokens";
import { formatDate } from "@/utils/date.util";
import { formatAddress } from "@/utils/user.util";

interface ReadOnlyFieldProps extends ViewWithTestID {
  type: FormFieldType;
  value?: boolean | Date | GooglePlacesAddress | string;
}

/**
 * Formats a field value based on its type
 *
 * Uses discriminated union to ensure type and value are correlated,
 * eliminating the need for type assertions.
 *
 * @param type - The field type
 * @param value - The raw value (type corresponds to field type)
 * @returns Formatted string for display, or undefined for empty values
 */
const formatFieldValue = ({
  type,
  value,
}: FormFieldTypeValue): string | undefined => {
  if (isNil(value)) {
    return undefined;
  }

  switch (type) {
    case FormFieldType.Address: {
      return formatAddress({
        address: value,
        shouldDivideWithNewline: false,
      });
    }

    case FormFieldType.CheckBox: {
      return value ? "Yes" : "No";
    }

    case FormFieldType.Date: {
      return formatDate({
        date: value,
        format: DateFormat.shortMonthDayYear,
      });
    }

    case FormFieldType.Select:
    case FormFieldType.Text: {
      return value.trim() || undefined;
    }
  }
};

/**
 * ReadOnlyField - Unified component for displaying form field values in read-only mode
 *
 * Handles formatting for all field types (Text, Date, Address, CheckBox, Select)
 * and delegates rendering to ReadOnlyFieldText.
 *
 * Generic component that uses type parameter inference to ensure type safety.
 * TypeScript infers T from the type prop and enforces that value matches.
 *
 * @param type - The field type to determine how to format the value
 * @param value - The value to display (type must match field type)
 * @param testID - Test identifier for the component
 *
 * @example
 * <ReadOnlyField
 *   type={FormFieldType.Date}
 *   value={new Date("2024-01-15")}
 *   testID="dob"
 * />
 */
const ReadOnlyField = ({ testID, type, value }: ReadOnlyFieldProps) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const formattedValue = formatFieldValue({
    type,
    value,
  } as FormFieldTypeValue);

  if (formattedValue) {
    return (
      <Text testID={testID} variant="bodyMedium">
        {formattedValue}
      </Text>
    );
  }
  return (
    <Text color={ColorToken.NeutralSecondary} testID={testID}>
      Not added yet
    </Text>
  );
};

export type { ReadOnlyFieldProps };
export default ReadOnlyField;
